import { cn } from '@/utils/cn'

interface SkeletonProps {
  className?: string
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded'
  width?: string | number
  height?: string | number
}

export function Skeleton({ 
  className, 
  variant = 'text',
  width,
  height 
}: SkeletonProps) {
  const baseClasses = "animate-pulse bg-brand-surface-alt"
  
  const variantClasses = {
    text: "rounded",
    circular: "rounded-full",
    rectangular: "rounded-none",
    rounded: "rounded-lg"
  }

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={{ width, height }}
    />
  )
}

// Pre-built skeleton layouts
export function ConversationItemSkeleton() {
  return (
    <div className="p-3 rounded-xl space-y-2">
      <div className="flex items-start justify-between gap-2">
        <Skeleton variant="text" className="h-4 w-3/4" />
        <Skeleton variant="circular" className="h-4 w-4" />
      </div>
      <div className="flex items-center justify-between">
        <Skeleton variant="text" className="h-3 w-16" />
        <Skeleton variant="text" className="h-3 w-10" />
      </div>
    </div>
  )
}

export function FolderSkeleton() {
  return (
    <div className="rounded-lg border border-brand-border/50 p-2 space-y-2">
      <div className="flex items-center gap-2">
        <Skeleton variant="circular" className="h-3 w-3" />
        <Skeleton variant="circular" className="h-3 w-3" />
        <Skeleton variant="text" className="h-3 w-24" />
        <Skeleton variant="circular" className="h-3 w-3 ml-auto" />
      </div>
      <div className="ml-4 space-y-1">
        <ConversationItemSkeleton />
      </div>
    </div>
  )
}

export function MessageSkeleton({ isUser = false }: { isUser?: boolean }) {
  return (
    <div className={cn("flex gap-3 mb-4", isUser ? "justify-end" : "justify-start")}>
      {!isUser && <Skeleton variant="circular" className="h-9 w-9 flex-shrink-0" />}
      <Skeleton 
        variant="rounded" 
        className={cn("h-16", isUser ? "w-[60%]" : "w-[75%]")} 
      />
      {isUser && <Skeleton variant="circular" className="h-9 w-9 flex-shrink-0" />}
    </div>
  )
}

export function PostCardSkeleton() {
  return (
    <div className="card overflow-hidden">
      <Skeleton variant="rectangular" className="h-48 w-full" />
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton variant="rounded" className="h-5 w-20" />
          <Skeleton variant="text" className="h-3 w-16" />
        </div>
        <Skeleton variant="text" className="h-5 w-full" />
        <Skeleton variant="text" className="h-4 w-full" />
        <Skeleton variant="text" className="h-4 w-2/3" />
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" className="h-5 w-5" />
            <Skeleton variant="text" className="h-3 w-20" />
          </div>
          <Skeleton variant="text" className="h-3 w-12" />
        </div>
      </div>
    </div>
  )
}
